// Pure helpers for the Research Dialogue Library admin (no React, no network).
// Contract: aris-ai owns every write; core-lambda serves /api/admin/research-library.

import { knownLifeAreas } from "../../Explore/lifeAreas";

/**
 * The release's own file naming — mirrors aris-ai's reader globs
 * `Aris_Dialogues_*_v26.*.csv` and `Aris_Dialogues_*_v26.*.csv.gz`.
 */
export const RELEASE_FILE_RE = /^Aris_Dialogues_.+_v26\..*\.csv(\.gz)?$/;

export const RELEASE_VERSION_RE = /^v\d+\.\d+$/;

export const IMPORT_STATUS = {
  AWAITING_UPLOAD: "AWAITING_UPLOAD",
  VALIDATING: "VALIDATING",
  VALIDATED: "VALIDATED",
  INVALID: "INVALID",
  COMMITTING: "COMMITTING",
  COMMITTED: "COMMITTED",
  FAILED: "FAILED",
};

/** Statuses at which validation polling stops. */
export const VALIDATION_TERMINAL = ["VALIDATED", "INVALID", "FAILED", "COMMITTED"];
/** Statuses at which commit polling stops. */
export const COMMIT_TERMINAL = ["COMMITTED", "FAILED"];

export const POLL_INTERVAL_MS = 3000;

export const DIALOGUE_TYPE_LABELS = {
  FOUNDATIONAL: "Foundational",
  COMPOSITE: "Composite",
};

const IMPORT_STATUS_META = {
  AWAITING_UPLOAD: { label: "Awaiting upload", variant: "secondary" },
  VALIDATING: { label: "Validating", variant: "info" },
  VALIDATED: { label: "Validated", variant: "success" },
  INVALID: { label: "Invalid", variant: "destructive" },
  COMMITTING: { label: "Committing", variant: "info" },
  COMMITTED: { label: "Committed", variant: "success" },
  FAILED: { label: "Failed", variant: "destructive" },
};

export const importStatusMeta = (status) =>
  IMPORT_STATUS_META[status] || { label: status || "Unknown", variant: "outline" };

export const isValidationTerminal = (status) => VALIDATION_TERMINAL.includes(status);
export const isCommitTerminal = (status) => COMMIT_TERMINAL.includes(status);

// ─── Dialogue status ────────────────────────────────────────────────────────

/** The reason a dialogue is held: an admin hold wins over the release's own rule. */
export const holdReason = (dialogue) => {
  if (!dialogue) return null;
  return (
    dialogue.admin_overrides?.hold?.reason ||
    dialogue.attachment_blocked_reason ||
    null
  );
};

export const isAdminHeld = (dialogue) => Boolean(dialogue?.admin_overrides?.hold);

/** "Live" when the runtime may attach it, else "Held". */
export const statusLabel = (dialogue) =>
  dialogue?.runtime_attachable ? "Live" : "Held";

export const typeLabel = (dialogueType) =>
  DIALOGUE_TYPE_LABELS[String(dialogueType || "").toUpperCase()] ||
  dialogueType ||
  "—";

/** Whether any admin override (title, description or hold) is set. */
export const hasOverrides = (dialogue) => {
  const o = dialogue?.admin_overrides;
  if (!o) return false;
  return Boolean(o.member_title != null || o.description != null || o.hold);
};

export const displayTitle = (dialogue) =>
  dialogue?.card_title || dialogue?.title || dialogue?.research_dialogue_id || "—";

// ─── Filters ────────────────────────────────────────────────────────────────

/**
 * Life-area filter options. The API's `life_area` is the display label
 * ("Career & Work"), which is each Life Area's `title`; any label the summary
 * reports that the platform doesn't know is appended so it stays filterable.
 */
export const lifeAreaOptions = (summary) => {
  const known = knownLifeAreas().map((area) => area.title);
  const extra = Object.keys(summary?.by_life_area || {}).filter(
    (label) => label && !known.includes(label)
  );
  return [...known, ...extra.sort()].map((label) => ({ value: label, label }));
};

export const holdReasonOptions = (summary) =>
  Object.entries(summary?.held || {})
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([reason, count]) => ({ value: reason, label: reason, count }));

/** Query params for GET /dialogues from the page's filter state ("all" → omitted). */
export const buildListParams = ({
  page,
  pageSize,
  search,
  lifeArea,
  dialogueType,
  status,
  reason,
}) => {
  const params = { page, page_size: pageSize };
  const pick = (v) => (v && v !== "all" ? v : undefined);
  if (search && search.trim()) params.search = search.trim();
  if (pick(lifeArea)) params.life_area = lifeArea;
  if (pick(dialogueType)) params.dialogue_type = dialogueType;
  if (pick(status)) params.status = status;
  if (pick(reason)) params.reason = reason;
  return params;
};

// ─── Release files ──────────────────────────────────────────────────────────

export const isReleaseFileName = (name) => RELEASE_FILE_RE.test(String(name || ""));

/**
 * Validate the chosen release files client-side.
 * @returns {{ ok: boolean, errors: string[] }}
 */
export const validateReleaseFiles = (files) => {
  const list = Array.from(files || []);
  const errors = [];
  if (list.length === 0) {
    errors.push("Choose the release's CSV files.");
    return { ok: false, errors };
  }
  const seen = new Set();
  list.forEach((file) => {
    const name = file?.name || "";
    if (!isReleaseFileName(name)) {
      errors.push(
        `${name || "(unnamed)"} is not a release file (expected Aris_Dialogues_…_v26.x….csv or .csv.gz).`
      );
    }
    if (seen.has(name)) errors.push(`${name} is selected twice.`);
    seen.add(name);
    if (file?.size === 0) errors.push(`${name} is empty.`);
  });
  return { ok: errors.length === 0, errors };
};

/** "v26.7" when every file names the same version, else null (the server decides). */
export const inferReleaseVersion = (names) => {
  const versions = new Set(
    Array.from(names || [])
      .map((n) => /_(v26\.\d+)[._]/.exec(String(n || ""))?.[1])
      .filter(Boolean)
  );
  return versions.size === 1 ? [...versions][0] : null;
};

// ─── Validation report ──────────────────────────────────────────────────────

/** Issues as rows, highest count first; zero counts are dropped. */
export const sortIssues = (issues, descriptions = {}) =>
  Object.entries(issues || {})
    .filter(([, count]) => Number(count) > 0)
    .map(([code, count]) => ({
      code,
      count: Number(count),
      description: descriptions?.[code] || "",
    }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));

const problemText = (p) => {
  if (p == null) return "";
  if (typeof p === "string") return p;
  return p.message || p.detail || p.description || JSON.stringify(p);
};

/** Integrity + contract problems. Any one blocks commit. */
export const reportBlockers = (report) => [
  ...(report?.integrity_problems || []).map((p) => ({
    kind: "Integrity",
    message: problemText(p),
  })),
  ...(report?.contract_problems || []).map((p) => ({
    kind: "Contract",
    message: problemText(p),
  })),
];

/** A release may be committed only when VALIDATED with no blocking problems. */
export const canCommit = (importDoc) =>
  importDoc?.status === IMPORT_STATUS.VALIDATED &&
  Boolean(importDoc?.report) &&
  reportBlockers(importDoc.report).length === 0;

export const REPORT_COUNT_LABELS = [
  ["dialogues", "Dialogues"],
  ["foundational", "Foundational"],
  ["composites", "Composites"],
  ["foundations", "Foundations"],
  ["attachable", "Attachable"],
  ["held_composites", "Held composites"],
];

// ─── Evidence payload ───────────────────────────────────────────────────────

const SOURCE_FIELDS = [
  ["institution", "Institution / Researcher"],
  ["title", "Title"],
  ["evidenceType", "Evidence Type"],
  ["url", "URL"],
];

/**
 * The payload's numbered findings (`Research Finding n`, `Claim ID n`,
 * `Research Headline n`, `Source Card n`, `Source n …`) as rows, in order.
 */
export const evidenceFindings = (payload) => {
  if (!payload) return [];
  const ns = new Set();
  Object.keys(payload).forEach((key) => {
    const m = /^(?:Research Finding|Claim ID|Source Card) (\d+)$/.exec(key);
    if (m) ns.add(Number(m[1]));
  });
  return [...ns]
    .sort((a, b) => a - b)
    .map((n) => {
      const row = {
        n,
        finding: payload[`Research Finding ${n}`] || null,
        claimId: payload[`Claim ID ${n}`] || null,
        headline: payload[`Research Headline ${n}`] || null,
        sourceId: payload[`Source Card ${n}`] || null,
      };
      SOURCE_FIELDS.forEach(([key, label]) => {
        row[key] = payload[`Source ${n} ${label}`] || null;
      });
      return row;
    })
    .filter((row) => row.finding || row.claimId);
};

// ─── Formatting ─────────────────────────────────────────────────────────────

export const formatCount = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString();

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export const errorMessage = (err, fallback = "Something went wrong") => {
  const data = err?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  return data?.message || err?.message || fallback;
};

export const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Overrides ──────────────────────────────────────────────────────────────

/**
 * PATCH body for an override edit: a key is sent only when it changes;
 * `null` clears that override (back to the release's own text).
 */
export const buildOverridePatch = ({
  initialTitle,
  title,
  clearTitle = false,
  initialDescription,
  description,
  clearDescription = false,
}) => {
  const patch = {};
  if (clearTitle) patch.member_title = null;
  else if ((title ?? "").trim() !== (initialTitle ?? "").trim()) {
    patch.member_title = (title ?? "").trim() || null;
  }
  if (clearDescription) patch.description = null;
  else if ((description ?? "").trim() !== (initialDescription ?? "").trim()) {
    patch.description = (description ?? "").trim() || null;
  }
  return patch;
};

/** The evidence detail's payload dict, whether sent bare or as the stored document. */
export const evidencePayloadDict = (evidence) => {
  if (!evidence || typeof evidence !== "object") return null;
  if (evidence.payload && typeof evidence.payload === "object") return evidence.payload;
  return evidence;
};
